import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Package, RefreshCw, Loader2, ChevronDown, ChevronRight, Store, Utensils, CalendarDays, Boxes,
  Refrigerator, FileSpreadsheet, FileDown,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; } };

export default function ApprovisionnementCCDPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateField, setDateField] = useState('opening');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [exporting, setExporting] = useState(null);

  const buildParams = () => {
    const params = new URLSearchParams({ date_field: dateField });
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    return params;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_field: dateField });
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const r = await fetch(`${API}/api/approvisionnement-ccd?${params.toString()}`, { headers });
      if (r.ok) setData(await r.json());
      else toast.error('Erreur de chargement');
    } catch {
      toast.error('Erreur réseau');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateField, dateFrom, dateTo, token]);

  useEffect(() => { load(); }, [load]);

  const exportFile = async (format) => {
    setExporting(format);
    try {
      const r = await fetch(`${API}/api/approvisionnement-ccd/export/${format}?${buildParams().toString()}`, { headers });
      if (!r.ok) throw new Error('fail');
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Approvisionnement_CCD_${new Date().toISOString().slice(0, 10)}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Export téléchargé');
    } catch {
      toast.error("Erreur lors de l'export");
    } finally {
      setExporting(null);
    }
  };

  const StoreList = ({ stores }) => (
    <div className="mt-2 border-t border-slate-100 pt-2 space-y-1">
      {stores.map((s, i) => (
        <div key={i} className="flex items-center justify-between text-xs px-1 py-1 rounded hover:bg-slate-50">
          <div className="flex items-center gap-2 min-w-0">
            <Store className="w-3 h-3 text-slate-400 flex-shrink-0" />
            <span className="truncate text-slate-700">{s.project_name}</span>
            <Badge variant="secondary" className="text-[9px]">{s.business_status === 'signed' ? 'Signé' : 'Gagné'}</Badge>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0 text-slate-500">
            <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{fmtDate(s.opening_date)}</span>
            <span className="font-bold text-indigo-700 tabular-nums">×{s.qty}</span>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="approvisionnement-ccd-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Package className="w-6 h-6 text-indigo-600" /> Approvisionnement CCD
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Compilation du matériel à commander sur les dossiers gagnés / signés (CAPEX porté par CCD)
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => exportFile('excel')} disabled={exporting === 'excel'}
              className="gap-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="appro-export-excel">
              {exporting === 'excel' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Excel
            </Button>
            <Button variant="outline" onClick={() => exportFile('pdf')} disabled={exporting === 'pdf'}
              className="gap-2 border-red-200 text-red-600 hover:bg-red-50" data-testid="appro-export-pdf">
              {exporting === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} PDF
            </Button>
            <Button variant="outline" onClick={load} className="gap-2" data-testid="appro-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualiser
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl border border-slate-200 bg-white">
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">Filtrer par</label>
            <select value={dateField} onChange={(e) => setDateField(e.target.value)}
              className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm" data-testid="appro-date-field">
              <option value="opening">Date d'ouverture estimée</option>
              <option value="created">Date de création</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">Du</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm" data-testid="appro-date-from" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">Au</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm" data-testid="appro-date-to" />
          </div>
          {(dateFrom || dateTo) && (
            <Button variant="ghost" size="sm" className="h-9 text-xs text-slate-500"
              onClick={() => { setDateFrom(''); setDateTo(''); }} data-testid="appro-filter-reset">
              Réinitialiser
            </Button>
          )}
          {data && (
            <div className="ml-auto text-xs text-slate-500 self-center">
              {data.dossiers_count} dossier(s) gagné(s)/signé(s)
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : data ? (
          <>
            {/* Totals strip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-5" data-testid="total-vitrines">
                <p className="text-xs uppercase tracking-wide text-indigo-500 flex items-center gap-1"><Boxes className="w-4 h-4" /> Vitrines (CCD)</p>
                <p className="text-4xl font-bold text-indigo-800 tabular-nums mt-2">{data.total_vitrines}</p>
              </div>
              <div className="rounded-xl border border-sky-200 bg-sky-50/40 p-5" data-testid="total-bacs">
                <p className="text-xs uppercase tracking-wide text-sky-600 flex items-center gap-1"><Refrigerator className="w-4 h-4" /> Bacs réfrigérés (CCD)</p>
                <p className="text-4xl font-bold text-sky-700 tabular-nums mt-2">{data.total_bacs}</p>
              </div>
              <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-5" data-testid="total-rice">
                <p className="text-xs uppercase tracking-wide text-amber-600 flex items-center gap-1"><Utensils className="w-4 h-4" /> Machines à riz</p>
                <p className="text-4xl font-bold text-amber-700 tabular-nums mt-2">{data.rice_machines.total_qty}</p>
              </div>
            </div>

            {/* Vitrines by size */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2"><Boxes className="w-5 h-5 text-indigo-600" /> Vitrines par taille (CAPEX porté par CCD)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.vitrines.map((v) => (
                  <div key={v.key} className={`rounded-lg border ${v.total_qty > 0 ? 'border-indigo-100' : 'border-slate-100'}`} data-testid={`vitrine-row-${v.key}`}>
                    <button
                      className="w-full flex items-center justify-between p-3 text-left"
                      onClick={() => v.total_qty > 0 && setExpanded(expanded === v.key ? null : v.key)}
                      disabled={v.total_qty === 0}
                    >
                      <div className="flex items-center gap-2">
                        {v.total_qty > 0 ? (expanded === v.key ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />) : <span className="w-4" />}
                        <span className="text-sm font-medium text-slate-800">{v.label}</span>
                        <span className="text-xs text-slate-400">{v.unit_price.toLocaleString('fr-FR')} € / u.</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {v.total_qty > 0 && <span className="text-xs text-slate-400">{v.stores.length} magasin(s)</span>}
                        <span className={`text-2xl font-bold tabular-nums ${v.total_qty > 0 ? 'text-indigo-700' : 'text-slate-300'}`}>{v.total_qty}</span>
                      </div>
                    </button>
                    {expanded === v.key && v.stores.length > 0 && (
                      <div className="px-3 pb-3"><StoreList stores={v.stores} /></div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Bacs réfrigérés (famille distincte) */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2"><Refrigerator className="w-5 h-5 text-sky-600" /> Bacs réfrigérés (CAPEX porté par CCD)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.bacs.map((b) => (
                  <div key={b.key} className={`rounded-lg border ${b.total_qty > 0 ? 'border-sky-100' : 'border-slate-100'}`} data-testid={`bac-row-${b.key}`}>
                    <button
                      className="w-full flex items-center justify-between p-3 text-left"
                      onClick={() => b.total_qty > 0 && setExpanded(expanded === b.key ? null : b.key)}
                      disabled={b.total_qty === 0}
                    >
                      <div className="flex items-center gap-2">
                        {b.total_qty > 0 ? (expanded === b.key ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />) : <span className="w-4" />}
                        <span className="text-sm font-medium text-slate-800">{b.label}</span>
                        <span className="text-xs text-slate-400">{b.unit_price.toLocaleString('fr-FR')} € / u.</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {b.total_qty > 0 && <span className="text-xs text-slate-400">{b.stores.length} magasin(s)</span>}
                        <span className={`text-2xl font-bold tabular-nums ${b.total_qty > 0 ? 'text-sky-700' : 'text-slate-300'}`}>{b.total_qty}</span>
                      </div>
                    </button>
                    {expanded === b.key && b.stores.length > 0 && (
                      <div className="px-3 pb-3"><StoreList stores={b.stores} /></div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Rice machines detail */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2"><Utensils className="w-5 h-5 text-amber-600" /> Machines à tapisser le riz ({data.rice_machines.total_qty})</CardTitle>
              </CardHeader>
              <CardContent>
                {data.rice_machines.stores.length > 0 ? (
                  <StoreList stores={data.rice_machines.stores} />
                ) : (
                  <p className="text-sm text-slate-400 text-center py-4">Aucune machine à commander sur ce périmètre.</p>
                )}
              </CardContent>
            </Card>
          </>
        ) : null}
      </div>
    </AppLayout>
  );
}
